import { sort } from 'fast-sort'
import { z } from 'zod'
import { uniq } from '../../../core/array/uniq'
import { kebabCase } from '../../../core/string/kebab-case'

export const Options = z.enum([
  'finish',
  'material',
  'mounting_holes',
  'size',
  'thickness_decimal',
  'underside_lip'
])

export type Options = z.infer<typeof Options>

export const SizeOptionValue = z
  .string()
  .min(1)
  .transform<string>((size) => {
    return size
      .split('|')
      .map((part) => part.trim())
      .map((part) => {
        return part
          .split(':')
          .map((subpart) => subpart.trim())
          .join(': ')
      })
      .join(' | ')
  })

export const OptionsValues = z.object({
  [Options.enum.finish]: z
    .array(z.object({ value: z.string(), image: z.string().optional() }))
    .optional()
    .default([]),
  [Options.enum.material]: z.array(z.string()).optional().default([]),
  [Options.enum.mounting_holes]: z.array(z.boolean()).optional().default([]),
  [Options.enum.size]: z
    .array(SizeOptionValue)
    .optional()
    .default([])
    .transform(uniq),
  [Options.enum.thickness_decimal]: z.array(z.string()).optional().default([]),
  [Options.enum.underside_lip]: z.array(z.boolean()).optional().default([])
})
export type OptionsValues = z.infer<typeof OptionsValues>

export const RedirectValues = z.object({
  Rectangle_Square: z.string().nullable().optional(),
  Round: z.string().nullable().optional(),
  Toe_Kick: z.string().nullable().optional()
});

export type RedirectValues = z.infer<typeof RedirectValues>

export type State = {
  [Options.enum.finish]?: string
  [Options.enum.material]?: string
  [Options.enum.mounting_holes]?: boolean
  [Options.enum.size]?: string
  [Options.enum.thickness_decimal]?: string
  [Options.enum.underside_lip]?: boolean
  parentId: string
}

export const Product = z.object({
  id: z.string(),
  variantId: z.string(),
  price: z
    .number()
    .or(z.string())
    .transform<number>((p) => {
      return typeof p === 'number' ? p : parseFloat(p)
    }),
  currency: z.string(),
  sku: z.string().nullable().optional().default(null),
  tags: z.array(z.string()),
  images: z.array(z.string()),
  alts: z.array(z.string().nullable()),
  metafields: z.record(z.any()).nullable().optional(),
  designerCollaboration: z.string().nullable().optional()
})
export type Product = z.infer<typeof Product>

export const Settings = z.object({
  help_copy: z.string(),
  how_to_measure_link: z.string(),
  installation_location_subtitle: z.string(),
  matching_louver_checkbox: z.string(),
  matching_louver_image: z.string(),
  matching_louver_subtitle: z.string(),
  beveled_edge_checkbox: z.string(),
  beveled_edge_image: z.string(),
  beveled_edge_subtitle: z.string(),
  mounting_holes_image: z.string(),
  mounting_holes_subtitle: z.string(),
  phone: z.string(),
  recommended_copy: z.string(),
  support_image: z.string(),
  underside_lip_image: z.string(),
  underside_lip_subtitle: z.string(),
  email: z.string(),
  help_links: z.string(),
  // Explore Collection:
  explore_image: z.string().nullable().optional(),
  explore_heading: z.string().nullable().optional(),
  explore_content: z.string().nullable().optional(),
  explore_link_label: z.string().nullable().optional(),
  explore_link_url: z.string().nullable().optional(),
  explore_link_new_tab: z.boolean().nullable().optional(),
  explore_image_position: z.enum(["left", "right"]).nullable().optional(),
  explore_panel_bg: z.string().nullable().optional(),
  explore_panel_text: z.string().nullable().optional()
})
export type Settings = z.infer<typeof Settings>

export const RawInitialData = z.object({
  product: Product,
  parentId: z.string(),
  title: z.string(),
  parsedId: z.string(),
  productHandle: z.string(),
  description: z.string(),
  collectionTitle: z.string().nullable(),
  discounts: z.object({
    tradePro: z
      .number()
      .nullable()
      .transform<number>((v) => (v == null ? 0 : v))
  }),
  finishSortPriority: z
    .array(z.string())
    .transform((items) => items.filter((item) => item.length > 0)),
  optionsValues: OptionsValues,
  redirectValues: RedirectValues.nullable().optional(),
  settings: Settings,
  helpLinks: z.array(
    z.object({
      url: z.string(),
      title: z.string()
    })
  )
})
export type RawInitialData = z.infer<typeof RawInitialData>

export const InitialData = RawInitialData.transform<RawInitialData>((data) => {
  const normalizedSortPriority = data.finishSortPriority.map(kebabCase)
  const sortedFinish = sort(data.optionsValues.finish).by([
    {
      asc: (item): number => {
        const normalizedValue = kebabCase(item.value)
        const fullMatchIndex = normalizedSortPriority.findIndex(
          (v) => v === normalizedValue
        )

        if (fullMatchIndex > -1) {
          return fullMatchIndex
        }

        const matchIndex = normalizedSortPriority.findIndex(
          (v) =>
            v.replace('-steel', '') === normalizedValue ||
            v.replace('-aluminum', '') === normalizedValue
        )

        return matchIndex === -1 ? 999999 : matchIndex
      }
    },
    {
      asc: (item) => item.value
    }
  ])

  const nextOptionsValues: OptionsValues = {
    ...data.optionsValues,
    finish: sortedFinish
  }

  return {
    ...data,
    optionsValues: nextOptionsValues
  }
})
export type InitialData = z.infer<typeof InitialData>